'use client'

import AddressList from '@/components/address/address-list'
import { EntityCatalogTab } from '@/components/catalog/EntityCatalogTab'
import { PayoutScheduleDisplay } from '@/components/payout-schedule/PayoutScheduleDisplay'
import { Professional, ScholarLevel } from '@/lib/api/common/interfaces/professional.interface'
import { getProfessional } from '@/lib/api/professionals/professionals-api'
import { useAuth } from '@/store/hooks'
import { formatPhoneNumber } from '@/lib/form-state/util'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useEffect, useState } from 'react'
import { FaGraduationCap, FaIdCard, FaUserMd, FaWhatsapp } from 'react-icons/fa'
import {
    MdAccessTime,
    MdArrowBack,
    MdBadge,
    MdBlock,
    MdCalendarToday,
    MdCheckCircle,
    MdContentCopy,
    MdEdit,
    MdEmail,
    MdHandshake,
    MdInventory2,
    MdLocalHospital,
    MdLocationOn,
    MdOpenInNew,
    MdPayment,
    MdPerson,
    MdPhone,
    MdSchool,
    MdStar,
    MdVerified,
    MdWork
} from 'react-icons/md'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import NotificationPreferencesCard from '@/components/notification-preferences/notification-preferences-card'
import { useServiceLabel } from '@/lib/hooks/use-service-label'

export default function ProfessionalDetailPage() {
    const { isHealth } = useServiceLabel()
    const navigate = useNavigate()
    const { canUpdate } = useResourcePermissions('person', 'professionals')
    const { user } = useAuth()
    const { id } = useParams<{ id: string }>()
    const [professional, setProfessional] = useState<Professional | null>(null)
    const [loading, setLoading] = useState(true)
    const [activeTab, setActiveTab] = useState<'dados' | 'catalog'>('dados')

    useEffect(() => {
        if (id) {
            loadProfessional()
        }
    }, [id])

    const loadProfessional = async () => {
        if (!id) return

        setLoading(true)
        try {
            const response = await getProfessional(parseInt(id, 10))
            if (response.success && response.result) {
                setProfessional(response.result)
            } else {
                toast.error(response.error || 'Erro ao carregar profissional')
                navigate('/person/professionals')
            }
        } catch (error) {
            toast.error('Erro ao carregar profissional')
            navigate('/person/professionals')
        } finally {
            setLoading(false)
        }
    }

    const getScholarLevelLabel = (level?: ScholarLevel): string => {
        if (!level) return 'Não informado'
        const labels: Record<ScholarLevel, string> = {
            [ScholarLevel.FUNDAMENTAL]: 'Ensino Fundamental',
            [ScholarLevel.MEDIO]: 'Ensino Médio',
            [ScholarLevel.TECNICO]: 'Ensino Técnico',
            [ScholarLevel.SUPERIOR]: 'Ensino Superior',
            [ScholarLevel.POS_GRADUACAO]: 'Pós-Graduação',
            [ScholarLevel.MESTRADO]: 'Mestrado',
            [ScholarLevel.DOUTORADO]: 'Doutorado',
            [ScholarLevel.POS_DOUTORADO]: 'Pós-Doutorado',
        }
        return labels[level] || level.replace('_', ' ')
    }

    const getInitials = (name: string) => {
        return name
            .split(' ')
            .map(n => n[0])
            .slice(0, 2)
            .join('')
            .toUpperCase()
    }

    const copyToClipboard = (text: string, label: string) => {
        navigator.clipboard.writeText(text)
        toast.success(`${label} copiado!`, { autoClose: 2000 })
    }

    const openWhatsApp = (phone: string) => {
        const cleanPhone = phone.replace(/\D/g, '')
        window.open(`https://wa.me/55${cleanPhone}`, '_blank')
    }

    if (loading) {
        return (
            <div className="min-h-[80vh] flex flex-col items-center justify-center gap-6">
                <div className="relative">
                    <div className="w-20 h-20 rounded-full bg-gradient-to-br from-secondary/20 to-secondary/20 animate-pulse"></div>
                    <div className="absolute inset-0 flex items-center justify-center">
                        <div className="loading loading-spinner loading-lg text-secondary"></div>
                    </div>
                </div>
                <div className="text-center">
                    <p className="text-base-content/60 text-sm">
                        Carregando perfil do profissional...
                    </p>
                </div>
            </div>
        )
    }

    if (!professional) {
        return (
            <div className="min-h-[80vh] flex flex-col items-center justify-center gap-6">
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-error/10 to-error/5 flex items-center justify-center">
                    <MdPerson className="text-5xl text-error/50" />
                </div>
                <div className="text-center max-w-md">
                    <h3 className="text-xl font-bold text-base-content mb-2">
                        Profissional não encontrado
                    </h3>
                    <p className="text-base-content/60">
                        O profissional que você está procurando não existe ou foi removido do sistema.
                    </p>
                </div>
                <button
                    onClick={() => navigate('/person/professionals')}
                    className="btn btn-secondary gap-2"
                >
                    <MdArrowBack className="text-lg" />
                    Voltar para profissionais
                </button>
            </div>
        )
    }

    const hasContactInfo = professional.email || professional.phoneNumber
    const hasDocumentation = professional.document || professional.registrationNumber || professional.councilName

    return (
        <div className="min-h-screen bg-gradient-to-b from-base-200/50 to-base-100">
            {/* Header com gradiente */}
            <div className="bg-gradient-to-r from-secondary/10 via-secondary/5 to-secondary/10 border-b border-base-300/50">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
                    {/* Navegação */}
                    <div className="flex items-center justify-between mb-6">
                        <button
                            onClick={() => navigate('/person/professionals')}
                            className="btn btn-ghost btn-sm gap-2 -ml-2 text-base-content/70 hover:text-base-content"
                        >
                            <MdArrowBack className="text-lg" />
                            <span className="hidden sm:inline">Voltar para profissionais</span>
                            <span className="sm:hidden">Voltar</span>
                        </button>

                        {canUpdate && (
                            <button
                                onClick={() => navigate(`/person/professionals/edit/${id}`)}
                                className="btn btn-secondary btn-sm gap-2 shadow-lg shadow-secondary/25"
                            >
                                <MdEdit className="text-lg" />
                                <span className="hidden sm:inline">Editar Perfil</span>
                                <span className="sm:hidden">Editar</span>
                            </button>
                        )}
                    </div>

                    {/* Profile Header */}
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
                        {/* Avatar */}
                        <div className="relative">
                            {professional.photoUrl ? (
                                <img src={professional.photoUrl} alt={professional.name} className="w-24 h-24 sm:w-28 sm:h-28 rounded-2xl object-cover shadow-xl shadow-secondary/20" />
                            ) : (
                                <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-2xl bg-gradient-to-br from-secondary to-secondary flex items-center justify-center shadow-xl shadow-secondary/20">
                                    <span className="text-3xl sm:text-4xl font-bold text-white">
                                        {getInitials(professional.name)}
                                    </span>
                                </div>
                            )}
                            {professional.user?.active && !professional.user?.pendingSetup && (
                                <div className="absolute -bottom-1 -right-1 w-7 h-7 bg-success rounded-lg flex items-center justify-center shadow-lg border-2 border-base-100">
                                    <MdVerified className="text-white text-sm" />
                                </div>
                            )}
                        </div>

                        {/* Info */}
                        <div className="flex-1">
                            <div className="flex flex-wrap items-center gap-2 mb-1">
                                <h1 className="text-2xl sm:text-3xl font-bold text-base-content">
                                    {professional.name}
                                </h1>
                            </div>

                            {professional.titles && professional.titles.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-1">
                                    {professional.titles.map(t => (
                                        <span key={t.id} className="badge badge-outline badge-sm">{t.title}</span>
                                    ))}
                                </div>
                            )}

                            {professional.registrationNumber && (
                                <p className="text-base-content/60 mb-3 flex items-center gap-2">
                                    <MdBadge className="text-lg" />
                                    {professional.councilName} {professional.registrationNumber}
                                </p>
                            )}

                            {/* Especialidades */}
                            {professional.specialties && professional.specialties.length > 0 && (
                                <div className="flex flex-wrap gap-2">
                                    {professional.specialties
                                        .filter(s => !s.specialty.parentId)
                                        .map(specialty => (
                                            <span
                                                key={specialty.id}
                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-secondary/10 text-secondary text-sm font-medium border border-secondary/20"
                                            >
                                                <MdStar className="text-xs" />
                                                {specialty.specialty.name}
                                            </span>
                                        ))}
                                    {professional.specialties
                                        .filter(s => !!s.specialty.parentId)
                                        .map(specialty => (
                                            <span
                                                key={specialty.id}
                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-secondary/10 text-secondary text-sm font-medium border border-secondary/20"
                                            >
                                                <MdStar className="text-xs" />
                                                {specialty.specialty.parent?.name ? `${specialty.specialty.parent.name} - ` : ''}
                                                {specialty.specialty.name}
                                            </span>
                                        ))}
                                </div>
                            )}
                        </div>

                        {/* Quick Actions - Desktop */}
                        {hasContactInfo && (
                            <div className="hidden lg:flex flex-col gap-2">
                                {professional.phoneNumber && (
                                    <button
                                        onClick={() => openWhatsApp(professional.phoneNumber!)}
                                        className="btn btn-success btn-sm gap-2 shadow-lg shadow-success/20"
                                    >
                                        <FaWhatsapp className="text-lg" />
                                        WhatsApp
                                    </button>
                                )}
                                {professional.email && (
                                    <a
                                        href={`mailto:${professional.email}`}
                                        className="btn btn-outline btn-sm gap-2"
                                    >
                                        <MdEmail className="text-lg" />
                                        Email
                                    </a>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
                <div role="tablist" className="tabs tabs-bordered mb-6">
                    <button
                        role="tab"
                        className={`tab gap-2 ${activeTab === 'dados' ? 'tab-active' : ''}`}
                        onClick={() => setActiveTab('dados')}
                    >
                        <MdPerson className="text-lg" /> Dados
                    </button>
                    <button
                        role="tab"
                        className={`tab gap-2 ${activeTab === 'catalog' ? 'tab-active' : ''}`}
                        onClick={() => setActiveTab('catalog')}
                    >
                        <MdInventory2 className="text-lg" /> Catálogo do Profissional
                    </button>
                </div>

                {activeTab === 'catalog' &&
                    (user?.companyId ? (
                        <EntityCatalogTab
                            entityType="professional"
                            entityId={parseInt(id!, 10)}
                            companyId={user.companyId}
                            entityName={professional.name}
                        />
                    ) : (
                        <p className="text-center py-10 text-base-content/60">
                            Empresa do usuário não identificada.
                        </p>
                    ))}

                <div
                    className={`grid grid-cols-1 lg:grid-cols-3 gap-6 ${
                        activeTab === 'dados' ? '' : 'hidden'
                    }`}
                >
                    {/* Main Content */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Quick Actions - Mobile */}
                        {hasContactInfo && (
                            <div className="lg:hidden grid grid-cols-2 gap-3">
                                {professional.phoneNumber && (
                                    <button
                                        onClick={() => openWhatsApp(professional.phoneNumber!)}
                                        className="btn btn-success gap-2 shadow-lg shadow-success/20"
                                    >
                                        <FaWhatsapp className="text-xl" />
                                        WhatsApp
                                    </button>
                                )}
                                {professional.email && (
                                    <a
                                        href={`mailto:${professional.email}`}
                                        className="btn btn-outline gap-2"
                                    >
                                        <MdEmail className="text-xl" />
                                        Email
                                    </a>
                                )}
                            </div>
                        )}

                        {/* Informações de Contato */}
                        {hasContactInfo && (
                            <div className="card bg-base-100 shadow-xl shadow-base-300/10 border border-base-200">
                                <div className="card-body">
                                    <div className="flex items-center gap-3 mb-6">
                                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-info/20 to-info/10 flex items-center justify-center">
                                            <MdPerson className="text-xl text-info" />
                                        </div>
                                        <h2 className="card-title text-lg">Informações de Contato</h2>
                                    </div>

                                    <div className="grid sm:grid-cols-2 gap-4">
                                        {professional.email && (
                                            <div className="group p-4 rounded-xl bg-base-200/50 hover:bg-base-200 transition-colors">
                                                <div className="flex items-start justify-between">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center">
                                                            <MdEmail className="text-xl text-secondary" />
                                                        </div>
                                                        <div>
                                                            <p className="text-xs font-medium text-base-content/50 uppercase tracking-wider mb-0.5">
                                                                Email
                                                            </p>
                                                            <p className="text-sm font-semibold text-base-content break-all">
                                                                {professional.email}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <button
                                                        onClick={() => copyToClipboard(professional.email!, 'Email')}
                                                        className="btn btn-ghost btn-xs btn-square opacity-0 group-hover:opacity-100 transition-opacity"
                                                    >
                                                        <MdContentCopy className="text-sm" />
                                                    </button>
                                                </div>
                                            </div>
                                        )}

                                        {professional.phoneNumber && (
                                            <div className="group p-4 rounded-xl bg-base-200/50 hover:bg-base-200 transition-colors">
                                                <div className="flex items-start justify-between">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
                                                            <MdPhone className="text-xl text-success" />
                                                        </div>
                                                        <div>
                                                            <p className="text-xs font-medium text-base-content/50 uppercase tracking-wider mb-0.5">
                                                                Telefone
                                                            </p>
                                                            <p className="text-sm font-semibold text-base-content">
                                                                {formatPhoneNumber(professional.phoneNumber)}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <button
                                                        onClick={() => copyToClipboard(professional.phoneNumber!, 'Telefone')}
                                                        className="btn btn-ghost btn-xs btn-square opacity-0 group-hover:opacity-100 transition-opacity"
                                                    >
                                                        <MdContentCopy className="text-sm" />
                                                    </button>
                                                </div>
                                            </div>
                                        )}

                                        {professional.phone2 && (
                                            <div className="group p-4 rounded-xl bg-base-200/50 hover:bg-base-200 transition-colors">
                                                <div className="flex items-start justify-between">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
                                                            <MdPhone className="text-xl text-success" />
                                                        </div>
                                                        <div>
                                                            <p className="text-xs font-medium text-base-content/50 uppercase tracking-wider mb-0.5">
                                                                Telefone 2
                                                            </p>
                                                            <p className="text-sm font-semibold text-base-content">
                                                                {formatPhoneNumber(professional.phone2)}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <button
                                                        onClick={() => copyToClipboard(professional.phone2!, 'Telefone 2')}
                                                        className="btn btn-ghost btn-xs btn-square opacity-0 group-hover:opacity-100 transition-opacity"
                                                    >
                                                        <MdContentCopy className="text-sm" />
                                                    </button>
                                                </div>
                                            </div>
                                        )}

                                        {professional.phone3 && (
                                            <div className="group p-4 rounded-xl bg-base-200/50 hover:bg-base-200 transition-colors">
                                                <div className="flex items-start justify-between">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
                                                            <MdPhone className="text-xl text-success" />
                                                        </div>
                                                        <div>
                                                            <p className="text-xs font-medium text-base-content/50 uppercase tracking-wider mb-0.5">
                                                                Telefone 3
                                                            </p>
                                                            <p className="text-sm font-semibold text-base-content">
                                                                {formatPhoneNumber(professional.phone3)}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <button
                                                        onClick={() => copyToClipboard(professional.phone3!, 'Telefone 3')}
                                                        className="btn btn-ghost btn-xs btn-square opacity-0 group-hover:opacity-100 transition-opacity"
                                                    >
                                                        <MdContentCopy className="text-sm" />
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Formação Acadêmica */}
                        {(professional.formation || professional.scholarLevel) && (
                            <div className="card bg-base-100 shadow-xl shadow-base-300/10 border border-base-200">
                                <div className="card-body">
                                    <div className="flex items-center gap-3 mb-6">
                                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-secondary/20 to-secondary/10 flex items-center justify-center">
                                            <FaGraduationCap className="text-xl text-secondary" />
                                        </div>
                                        <h2 className="card-title text-lg">Formação Acadêmica</h2>
                                    </div>

                                    <div className="space-y-4">
                                        {professional.formation && (
                                            <div className="flex items-start gap-4 p-4 rounded-xl bg-gradient-to-r from-secondary/5 to-transparent border-l-4 border-secondary">
                                                <div className="w-12 h-12 rounded-xl bg-secondary/10 flex items-center justify-center flex-shrink-0">
                                                    <MdWork className="text-2xl text-secondary" />
                                                </div>
                                                <div>
                                                    <p className="text-xs font-medium text-base-content/50 uppercase tracking-wider mb-1">
                                                        Formação
                                                    </p>
                                                    <p className="text-base font-semibold text-base-content">
                                                        {professional.formation}
                                                    </p>
                                                </div>
                                            </div>
                                        )}

                                        {professional.scholarLevel && (
                                            <div className="flex items-start gap-4 p-4 rounded-xl bg-gradient-to-r from-secondary/5 to-transparent border-l-4 border-secondary">
                                                <div className="w-12 h-12 rounded-xl bg-secondary/10 flex items-center justify-center flex-shrink-0">
                                                    <MdSchool className="text-2xl text-secondary" />
                                                </div>
                                                <div>
                                                    <p className="text-xs font-medium text-base-content/50 uppercase tracking-wider mb-1">
                                                        Nível de Escolaridade
                                                    </p>
                                                    <p className="text-base font-semibold text-base-content">
                                                        {getScholarLevelLabel(professional.scholarLevel)}
                                                    </p>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Especializações */}
                        {professional.qualifications && professional.qualifications.length > 0 && (
                            <div className="card bg-base-100 shadow-xl shadow-base-300/10 border border-base-200">
                                <div className="card-body">
                                    <div className="flex items-center gap-3 mb-6">
                                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-accent/20 to-accent/10 flex items-center justify-center">
                                            <MdStar className="text-xl text-accent" />
                                        </div>
                                        <h2 className="card-title text-lg">Especializações</h2>
                                    </div>
                                    <div className="flex flex-col gap-2">
                                        {professional.qualifications.map(q => (
                                            <div key={q.id} className="flex items-center gap-2 p-3 bg-base-200/50 rounded-lg border border-base-300/30">
                                                <span className="text-sm">{q.description}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Documentação Profissional */}
                        {hasDocumentation && (
                            <div className="card bg-base-100 shadow-xl shadow-base-300/10 border border-base-200">
                                <div className="card-body">
                                    <div className="flex items-center gap-3 mb-6">
                                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-warning/20 to-warning/10 flex items-center justify-center">
                                            <FaIdCard className="text-xl text-warning" />
                                        </div>
                                        <h2 className="card-title text-lg">Documentação Profissional</h2>
                                    </div>

                                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                        {professional.documentType && professional.document && (
                                            <div className="p-4 rounded-xl bg-base-200/50 border border-base-300/50">
                                                <p className="text-xs font-medium text-base-content/50 uppercase tracking-wider mb-1">
                                                    {professional.documentType}
                                                </p>
                                                <p className="text-sm font-semibold text-base-content font-mono">
                                                    {professional.document}
                                                </p>
                                            </div>
                                        )}

                                        {professional.registrationNumber && (
                                            <div className="p-4 rounded-xl bg-base-200/50 border border-base-300/50">
                                                <p className="text-xs font-medium text-base-content/50 uppercase tracking-wider mb-1">
                                                    Registro Profissional
                                                </p>
                                                <p className="text-sm font-semibold text-base-content font-mono">
                                                    {professional.registrationNumber}
                                                </p>
                                            </div>
                                        )}

                                        {professional.councilName && (
                                            <div className="p-4 rounded-xl bg-base-200/50 border border-base-300/50">
                                                <p className="text-xs font-medium text-base-content/50 uppercase tracking-wider mb-1">
                                                    Conselho
                                                </p>
                                                <p className="text-sm font-semibold text-base-content">
                                                    {professional.councilName}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Endereços */}
                        <div className="card bg-base-100 shadow-xl shadow-base-300/10 border border-base-200">
                            <div className="card-body">
                                <div className="flex items-center gap-3 mb-6">
                                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-error/20 to-error/10 flex items-center justify-center">
                                        <MdLocationOn className="text-xl text-error" />
                                    </div>
                                    <h2 className="card-title text-lg">
                                        Endereços
                                    </h2>
                                </div>
                                <AddressList
                                    owner={{ professionalId: professional.id }}
                                    emptyHint="Nenhum endereço cadastrado para este profissional"
                                />
                            </div>
                        </div>
                        {/* Repasse de Valores */}
                        <PayoutScheduleDisplay
                            payoutScheduleType={professional.payoutScheduleType}
                            payoutFixedDay={professional.payoutFixedDay}
                            payoutDaysAfterAppointment={professional.payoutDaysAfterAppointment}
                            title="Repasse de Valores ao Profissional"
                        />

                        {/* Hospitais Vinculados — entidade da saúde. Um registro
                            antigo pode ter vínculo gravado; fora da saúde ele não
                            é exibido. */}
                        {isHealth &&
                            professional.hospitals &&
                            professional.hospitals.length > 0 && (
                            <div className="card bg-base-100 shadow-xl shadow-base-300/10 border border-base-200">
                                <div className="card-body">
                                    <div className="flex items-center gap-3 mb-6">
                                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-secondary/20 to-secondary/10 flex items-center justify-center">
                                            <MdLocalHospital className="text-xl text-secondary" />
                                        </div>
                                        <h2 className="card-title text-lg">
                                            Hospitais Vinculados
                                        </h2>
                                        <span className="badge badge-secondary badge-sm">
                                            {professional.hospitals.length}
                                        </span>
                                    </div>

                                    <div className="grid sm:grid-cols-2 gap-3">
                                        {professional.hospitals.map(ph => (
                                            <div
                                                key={ph.id}
                                                className="group flex items-center gap-3 p-4 rounded-xl bg-base-200/50 hover:bg-base-200 transition-colors cursor-pointer border border-base-300/30 hover:border-secondary/30"
                                                onClick={() => navigate(`/registrations/hospitals/${ph.hospital.id}`)}
                                            >
                                                <div className="w-10 h-10 rounded-lg bg-secondary/10 group-hover:bg-secondary/20 flex items-center justify-center flex-shrink-0 transition-colors">
                                                    <MdLocalHospital className="text-xl text-secondary" />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-semibold text-base-content group-hover:text-secondary transition-colors truncate">
                                                        {ph.hospital.name}
                                                    </p>
                                                </div>
                                                <MdOpenInNew className="text-base-content/30 group-hover:text-secondary/60 transition-colors flex-shrink-0" />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                        {/* Estabelecimentos Vinculados */}
                        {professional.establishments && professional.establishments.length > 0 && (
                            <div className="card bg-base-100 shadow-xl shadow-base-300/10 border border-base-200">
                                <div className="card-body">
                                    <div className="flex items-center gap-3 mb-6">
                                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-info/20 to-info/10 flex items-center justify-center">
                                            <MdWork className="text-xl text-info" />
                                        </div>
                                        <h2 className="card-title text-lg">
                                            Estabelecimentos Vinculados
                                        </h2>
                                        <span className="badge badge-secondary badge-sm">
                                            {professional.establishments.length}
                                        </span>
                                    </div>

                                    <div className="flex flex-col gap-2">
                                        {professional.establishments.map(e => (
                                            <div key={e.id} className="flex items-center gap-3 p-3 bg-base-200/50 rounded-lg border border-base-300/30">
                                                <div className="w-8 h-8 rounded-lg bg-info/10 flex items-center justify-center flex-shrink-0">
                                                    <MdWork className="text-lg text-info" />
                                                </div>
                                                <span className="text-sm font-medium">{e.establishment.name}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                    {/* Notificações: por quais canais este profissional aceita ser avisado.
                        Fica no detalhe, e não na edição, porque é decisão de
                        relacionamento — e vale na hora, sem salvar a tela inteira. */}
                    <NotificationPreferencesCard
                        entityType="professional"
                        entityId={professional.id}
                        preferences={professional.notificationPreferences}
                        entityName={professional.name}
                        canEdit={canUpdate}
                    />
                    </div>

                    {/* Sidebar */}
                    <div className="space-y-6">

                        {/* Status do Sistema — "tem acesso" = possui usuário */}
                        {!professional.user ? (
                            <div className="card bg-base-100 shadow-xl shadow-base-300/10 border border-base-200">
                                <div className="card-body">
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-base-content/10 to-base-content/5 flex items-center justify-center">
                                            <FaUserMd className="text-lg text-base-content/70" />
                                        </div>
                                        <h3 className="font-bold text-base-content">
                                            Acesso ao Sistema
                                        </h3>
                                    </div>
                                    <p className="text-sm text-base-content/60">Sem acesso ao sistema</p>
                                </div>
                            </div>
                        ) : professional.user ? (
                            <div className="card bg-base-100 shadow-xl shadow-base-300/10 border border-base-200">
                                <div className="card-body">
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-base-content/10 to-base-content/5 flex items-center justify-center">
                                            <FaUserMd className="text-lg text-base-content/70" />
                                        </div>
                                        <h3 className="font-bold text-base-content">
                                            Acesso ao Sistema
                                        </h3>
                                    </div>

                                    {professional.user.pendingSetup ? (
                                        <div className="p-4 rounded-xl bg-warning/10 border border-warning/20">
                                            <div className="flex items-center gap-3 mb-3">
                                                <div className="w-10 h-10 rounded-full bg-warning/20 flex items-center justify-center animate-pulse">
                                                    <MdAccessTime className="text-xl text-warning" />
                                                </div>
                                                <div>
                                                    <p className="font-semibold text-warning">
                                                        Aguardando Configuração
                                                    </p>
                                                    <p className="text-xs text-warning/70">
                                                        Pendente ativação
                                                    </p>
                                                </div>
                                            </div>
                                            <p className="text-sm text-base-content/70 leading-relaxed">
                                                Um email foi enviado para{' '}
                                                <strong className="text-base-content">
                                                    {professional.user.email}
                                                </strong>{' '}
                                                com instruções para criar a senha de acesso.
                                            </p>
                                        </div>
                                    ) : professional.user.active ? (
                                        <div className="p-4 rounded-xl bg-success/10 border border-success/20">
                                            <div className="flex items-center gap-3 mb-3">
                                                <div className="w-10 h-10 rounded-full bg-success/20 flex items-center justify-center">
                                                    <MdCheckCircle className="text-xl text-success" />
                                                </div>
                                                <div>
                                                    <p className="font-semibold text-success">
                                                        Usuário Ativo
                                                    </p>
                                                    <p className="text-xs text-success/70">
                                                        Acesso liberado
                                                    </p>
                                                </div>
                                            </div>
                                            <p className="text-sm text-base-content/70 leading-relaxed">
                                                O profissional pode acessar o sistema com o email{' '}
                                                <strong className="text-base-content">
                                                    {professional.user.email}
                                                </strong>
                                            </p>
                                        </div>
                                    ) : (
                                        <div className="p-4 rounded-xl bg-error/10 border border-error/20">
                                            <div className="flex items-center gap-3 mb-3">
                                                <div className="w-10 h-10 rounded-full bg-error/20 flex items-center justify-center">
                                                    <MdBlock className="text-xl text-error" />
                                                </div>
                                                <div>
                                                    <p className="font-semibold text-error">
                                                        Usuário Inativo
                                                    </p>
                                                    <p className="text-xs text-error/70">
                                                        Acesso bloqueado
                                                    </p>
                                                </div>
                                            </div>
                                            <p className="text-sm text-base-content/70 leading-relaxed">
                                                O acesso do profissional ao sistema está desativado.
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : null}

                        {/* Parceiro Vinculado */}
                        {professional.partner && (
                            <div className="card bg-base-100 shadow-xl shadow-base-300/10 border border-base-200">
                                <div className="card-body">
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-warning/20 to-warning/10 flex items-center justify-center">
                                            <MdHandshake className="text-xl text-warning" />
                                        </div>
                                        <h3 className="font-bold text-base-content">
                                            Parceiro Vinculado
                                        </h3>
                                    </div>
                                    <div className="p-4 rounded-xl bg-warning/5 border border-warning/20">
                                        <p className="font-semibold text-base-content">
                                            {professional.partner.name}
                                        </p>
                                        {professional.partner.tradingName && (
                                            <p className="text-sm text-base-content/60 mt-0.5">
                                                {professional.partner.tradingName}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Informacoes do Cadastro */}
                        <div className="card bg-base-100 shadow-xl shadow-base-300/10 border border-base-200">
                            <div className="card-body">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-base-content/10 to-base-content/5 flex items-center justify-center">
                                        <MdCalendarToday className="text-lg text-base-content/70" />
                                    </div>
                                    <h3 className="font-bold text-base-content">
                                        Informações do Cadastro
                                    </h3>
                                </div>

                                <div className="space-y-4">
                                    <div className="flex items-center justify-between p-3 rounded-lg bg-base-200/50">
                                        <div>
                                            <p className="text-xs font-medium text-base-content/50 uppercase tracking-wider">
                                                Criado em
                                            </p>
                                            <p className="text-sm font-semibold text-base-content">
                                                {format(
                                                    new Date(professional.createdAt),
                                                    "dd MMM yyyy",
                                                    { locale: ptBR }
                                                )}
                                            </p>
                                        </div>
                                        <p className="text-xs text-base-content/50">
                                            {format(
                                                new Date(professional.createdAt),
                                                "HH:mm",
                                                { locale: ptBR }
                                            )}
                                        </p>
                                    </div>

                                    <div className="flex items-center justify-between p-3 rounded-lg bg-base-200/50">
                                        <div>
                                            <p className="text-xs font-medium text-base-content/50 uppercase tracking-wider">
                                                Atualizado em
                                            </p>
                                            <p className="text-sm font-semibold text-base-content">
                                                {format(
                                                    new Date(professional.updatedAt),
                                                    "dd MMM yyyy",
                                                    { locale: ptBR }
                                                )}
                                            </p>
                                        </div>
                                        <p className="text-xs text-base-content/50">
                                            {format(
                                                new Date(professional.updatedAt),
                                                "HH:mm",
                                                { locale: ptBR }
                                            )}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Quick Actions Card */}
                        {canUpdate && (
                            <div className="card bg-gradient-to-br from-secondary/10 via-secondary/5 to-secondary/10 shadow-xl shadow-secondary/5 border border-secondary/20">
                                <div className="card-body">
                                    <h3 className="font-bold text-base-content mb-4">
                                        Ações Rápidas
                                    </h3>
                                    <div className="space-y-2">
                                        <button
                                            onClick={() => navigate(`/person/professionals/edit/${id}`)}
                                            className="btn btn-secondary btn-block justify-start gap-3"
                                        >
                                            <MdEdit className="text-lg" />
                                            Editar Perfil
                                        </button>
                                        <button
                                            onClick={() => navigate(`/atendimento/agenda`)}
                                            className="btn btn-outline btn-block justify-start gap-3"
                                        >
                                            <MdCalendarToday className="text-lg" />
                                            Ver Agenda
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}
